import {
  Box,
  chakra,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  Input,
} from '@chakra-ui/react';
import { Dropdown, Text, TextField } from '@diamond/shared/ui';
import {
  NOTE_MAX_LENGTH_DEFAULT,
  sanitizeNote,
} from '@diamond/sol-buyer/utils';
import { EditOutlined, LocalShippingOutlined } from '@mui/icons-material';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

interface ShipmentDates {
  dates_available: string[];
  dates_available_ui: string[];
}
interface DeliveryNotesProps {
  shipmentDates: ShipmentDates;
  onDeliveryChange?: (shippingTier: string, deliveryOption: string) => void;
}

const generateItems = (dates: ShipmentDates) => {
  return dates.dates_available.map((date_available, index) => {
    const label =
      index === 0
        ? 'Pengiriman Segera'
        : `H+${index + 1} Pemesanan • ${dates.dates_available_ui[index]}`;

    return { label, value: date_available };
  });
};

export function DeliveryNotes({
  shipmentDates,
  onDeliveryChange,
}: DeliveryNotesProps) {
  const {
    watch,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const hasInitialized = useRef(false);

  const items = useMemo(
    () =>
      shipmentDates?.dates_available?.length > 0 &&
      shipmentDates?.dates_available_ui?.length > 0
        ? generateItems(shipmentDates)
        : [],
    [shipmentDates]
  );

  useEffect(() => {
    if (items.length === 0) return;

    const currentDeliveryDate = getValues('delivery_date');
    const currentDeliveryOption = getValues('delivery_option');

    // Initialize default values if not set (only once)
    if (
      !hasInitialized.current &&
      (currentDeliveryDate === null || currentDeliveryDate === undefined)
    ) {
      setValue('delivery_date', items[0].value);
      setValue('delivery_option', 'immediate');
      hasInitialized.current = true;

      if (onDeliveryChange) {
        onDeliveryChange('Pengiriman Segera', 'immediate');
      }
      return;
    }

    // Track existing values for analytics
    if (
      hasInitialized.current &&
      currentDeliveryDate &&
      currentDeliveryOption &&
      onDeliveryChange
    ) {
      const index =
        shipmentDates?.dates_available?.findIndex(
          (date) => date === currentDeliveryDate
        ) ?? -1;

      if (index >= 0) {
        const shippingTier =
          index === 0 ? 'Pengiriman Segera' : `H+${index + 1}`;
        onDeliveryChange(shippingTier, currentDeliveryOption);
      }
    }
  }, [items, setValue, getValues, onDeliveryChange, shipmentDates]);

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setValue('delivery_date', selectedValue);

    let deliveryOption = '';
    let shippingTier = '';

    // Find the index of the selected date
    const index = shipmentDates.dates_available.findIndex(
      (date) => date === selectedValue
    );

    if (index === 0) {
      // First date is always immediate delivery
      deliveryOption = 'immediate';
      shippingTier = 'Pengiriman Segera';
    } else if (index > 0) {
      // Subsequent dates are custom dates
      deliveryOption = `custom_date_${index + 1}`;
      shippingTier = `H+${index + 1}`;
    } else {
      // Fallback in case the date is not found
      deliveryOption = 'immediate';
      shippingTier = 'Pengiriman Segera';
    }

    setValue('delivery_option', deliveryOption);

    // Call the callback to track shipping info
    if (onDeliveryChange) {
      onDeliveryChange(shippingTier, deliveryOption);
    }
  };

  return (
    <Box
      flex="1"
      border="1px"
      borderColor="gray.33"
      shadow="base"
      px="4"
      py="6"
      rounded="lg"
    >
      <Flex>
        <Flex justifyContent="center" alignItems="center" mr="2">
          <Icon as={LocalShippingOutlined} color="blue" boxSize={8} />
        </Flex>
        <Box>
          <Text fontSize="20px" fontWeight="bold">
            Catatan Pengiriman
          </Text>
        </Box>
      </Flex>
      <Text color="gray.75" fontSize="12px" textAlign="justify">
        Jam pengantaran bergantung pada rute kurir dan keadaan lalu lintas pada
        hari pengiriman. Permintaan tanggal kirim pada Catatan Produk tidak
        berlaku.
      </Text>
      <Grid templateColumns="repeat(1, 1fr)" gap={6} mt="6">
        <GridItem colSpan={{ base: 0, md: 2 }}>
          <Flex direction={['column', 'column', 'row']} gap="4">
            <Flex flex="1" direction="column" gap={2}>
              <Box>
                <FormLabel
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  w="full"
                  aria-label="Pilih tanggal pengiriman"
                  htmlFor="delivery_date"
                >
                  <Box>
                    Pilih tanggal pengiriman
                    <chakra.span fontSize="sm" color="red">
                      *
                    </chakra.span>
                  </Box>
                  <Icon as={EditOutlined} color="gray.75" boxSize={6} />
                </FormLabel>
                <Dropdown
                  id="delivery_date"
                  name="delivery_date"
                  size="md"
                  rounded="lg"
                  register={register}
                  placeholder=""
                  items={items}
                  errors={errors}
                  onChange={handleDateChange}
                />
              </Box>
              <Text fontSize="12px" textAlign="justify">
                {watch('delivery_option') !== 'immediate'
                  ? 'Tanggal maksimal 5 hari kerja sejak pembuatan PO. Pengiriman hanya dilakukan pada hari kerja, Tidak ada pengiriman di hari minggu dan tanggal merah.'
                  : 'Pembuatan PO dihari Senin - Jumat sebelum jam 15:00 atau Sabtu sebelum jam 12:00 akan dikirim H+1 hari kerja. Diluar jam tersebut, maka pesanan akan diproses dihari berikutnya.'}
              </Text>
              <Input type="hidden" {...register('delivery_option')} />
            </Flex>
            <Flex flex="1" direction="column" gap={2}>
              <Box>
                <FormLabel
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  w="full"
                  aria-label="Catatan Tambahan (Opsional)"
                  htmlFor="delivery_note"
                >
                  <Box>
                    Catatan Tambahan{' '}
                    <chakra.span fontSize="sm" color="gray.75">
                      Opsional
                    </chakra.span>
                  </Box>
                  <Icon as={EditOutlined} color="gray.75" boxSize={6} />
                </FormLabel>
                <TextField
                  id="delivery_note"
                  name="delivery_note"
                  placeholder="Contoh: Antarkan ke loading dock mall"
                  size="md"
                  rounded="lg"
                  maxLength={NOTE_MAX_LENGTH_DEFAULT}
                  register={register}
                  registerOptions={{
                    maxLength: {
                      value: NOTE_MAX_LENGTH_DEFAULT,
                      message: 'Maksimal 150 karakter',
                    },
                    // Disallow all Unicode control chars using property escapes
                    validate: (v: string) =>
                      /^[^\p{Cc}]*$/u.test(v) || 'Karakter tidak valid',
                  }}
                  errors={errors}
                  onInput={(e) => {
                    const input = e.currentTarget as HTMLInputElement;
                    input.value = sanitizeNote(
                      input.value,
                      NOTE_MAX_LENGTH_DEFAULT
                    );
                  }}
                />
              </Box>

              <Flex
                flexDirection={{ base: 'column', lg: 'row' }}
                alignItems="start"
                gap={{ base: '0', lg: '1.5' }}
              >
                <Text color="red" fontSize="12px">
                  Bukan untuk ganti lokasi pengiriman.
                </Text>
                <Text color="gray.75" fontSize="12px">
                  Khusus catatan terkait penerima barang.
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
}
